import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Divider,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Common/Breadcrumb';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const OCCUPATION_LABELS: Record<string, string> = {
  student: 'Student',
  employee: 'Employee',
  self_employed: 'Self-employed',
  business_owner: 'Business Owner',
  freelancer: 'Freelancer',
  others: 'Others',
};

const Profile: React.FC = () => {
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading profile..." />;
  }

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          Please log in to view your profile
        </Typography>
      </Box>
    );
  }

  const profile = user.profile;
  const displayName = profile?.complete_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="md">
        {/* Breadcrumb */}
        <Breadcrumb />

        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ mb: 6 }}
        >
          My Profile
        </Typography>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
              <Box sx={{ flex: '1 1 0', textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 2,
                    fontSize: '3rem',
                  }}
                >
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {displayName}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  @{user.username}
                </Typography>
              </Box>

              <Box sx={{ flex: '2 1 0' }}>
                <Typography variant="h6" gutterBottom>
                  Account Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    columnGap: 4,
                    rowGap: 3,
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Username
                    </Typography>
                    <Typography variant="body1">
                      {user.username}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {user.email}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Nome Completo
                    </Typography>
                    <Typography variant="body1">
                      {profile?.complete_name || 'Not set'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Numero WhatsApp
                    </Typography>
                    <Typography variant="body1">
                      {profile?.whatsapp_number || 'Not set'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Occupation
                    </Typography>
                    <Typography variant="body1">
                      {profile?.occupation ? OCCUPATION_LABELS[profile.occupation] || profile.occupation : 'Not set'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Member Since
                    </Typography>
                    <Typography variant="body1">
                      {new Date(user.date_joined).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleLogout}
                  >
                    Logout
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Profile;
